/**
 * campus-pulse-data — the Campus Pulse report's view model.
 *
 * NOTHING IS MEASURED HERE ANY MORE. Until kit 8d8d16c6 this module composed
 * the whole report client-side from listCustomers / listStaff / listLocations /
 * getServiceCatalogue, which meant the measurement lived in a browser and the
 * report could not say what it had failed to measure. `getCampusPulseReport`
 * (JAI-1403, v1.1) now returns cohorts, KPIs, findings, breakdowns, the
 * engagement frame and a paged roster — plus a `coverage` block naming every
 * dimension it could not answer.
 *
 * So this module is a MAPPER: server report → the shapes the screen renders,
 * and the screen's filter state → `CampusPulseReportParameters`. Deleted with
 * the switch: buildWorld, view, briefing, findings-as-measurement, termsFor,
 * signature, DEPT_GUESS / deptKind (the department classification is now the
 * `department_kind` node attribute, resolved server-side) and the "directory
 * intake" cohort labelling (cohorts are real class years now, or the explicit
 * `formed: false` all-students fallback).
 *
 * The one rule that survives unchanged: a finding whose inputs were removed is
 * `unreadable` with a reason, never a zero. The server decides that now, and
 * prints its own thresholds in `method`; this module passes both through
 * verbatim.
 */
import type {
  CampusPulseCoverageVerdict,
  CampusPulseDepartmentKind,
  CampusPulseFinding as ApiFinding,
  CampusPulseFindingKey,
  CampusPulseQuadrantKey,
  CampusPulseReport,
  CampusPulseReportParameters,
  CampusPulseRosterRow,
  CampusPulseRosterSortKey,
} from "@v0y4ge/api-contracts";
import { type Severity, type FindingCategory, type FindingVerb, type FindingCardModel } from "./findings-kit";
import { t } from "@v0y4ge/i18n-runtime";
export type { Severity, FindingCategory, FindingVerb };

/* ── Lanes ─────────────────────────────────────────────────────────────
 * The filter bar's four chips are no longer one dimension. Three of them are
 * MODALITIES (`appointment` / `queueEntry` / `eventRegistration`) and the
 * fourth is an OUTCOME (`no_show`), and v1.1 accepts them as two parameters
 * that both narrow at the fact query. No finding declares either in its
 * `inputs[]` — inputs are definition and table names — so unchecking a lane
 * re-measures every card against the narrowed set rather than blanking the
 * page. Only cards that fall under their own n-minimum go unreadable, and they
 * say which lane to switch back on. */
export type Lane = "appt" | "walkin" | "event" | "noshow";
export const LANES: { id: Lane; label: string }[] = [
  { id: "appt", get label() { return t("reports.campusPulseData.laneAppointments", "Appointments"); } },
  { id: "walkin", get label() { return t("reports.campusPulseData.laneWalkIns", "Walk-ins"); } },
  { id: "event", get label() { return t("reports.campusPulseData.laneEvents", "Events"); } },
  { id: "noshow", get label() { return t("reports.campusPulseData.laneNoShows", "No-shows"); } },
];

export type DeptKind = CampusPulseDepartmentKind | null;

export interface Filters {
  program: string;
  campus: string;
  search: string;
  lanes: Record<Lane, boolean>;
  sort: CampusPulseRosterSortKey;
  dir: "asc" | "desc";
}
export const DEFAULT_FILTERS: Filters = {
  program: "all", campus: "all", search: "",
  lanes: { appt: true, walkin: true, event: true, noshow: true },
  sort: "interactions", dir: "desc",
};

export type Scope =
  | { finding: CampusPulseFindingKey }
  | { quadrant: CampusPulseQuadrantKey }
  | null;

export const ROSTER_PAGE = 25;

/** The screen's whole state, expressed as the operation's parameters. One
 *  request per state change: paging, sorting and search are the server's. */
export function toParameters(
  f: Filters,
  classYear: number | null,
  page: number,
  scope: Scope,
  rosterLimit = ROSTER_PAGE,
): CampusPulseReportParameters {
  const modalities: ("appointment" | "queueEntry" | "eventRegistration")[] = [];
  if (f.lanes.appt) modalities.push("appointment");
  if (f.lanes.walkin) modalities.push("queueEntry");
  if (f.lanes.event) modalities.push("eventRegistration");

  /* The no-show chip is an outcome, not a lane. Off means "measure everything
   * except the no-shows"; on means say nothing about outcomes at all, so the
   * report is not silently narrowed to three states. `walked_out` is NOT a
   * value: the spine folds a queue entry's `left` onto `cancelled` and keeps
   * "left" only as a lifecycle reason, so asking for it would either mean
   * `cancelled` or match nothing. */
  const outcomes: ("completed" | "no_show" | "cancelled")[] | undefined =
    f.lanes.noshow ? undefined : ["completed", "cancelled"];

  return {
    ...(classYear !== null ? { classYears: [classYear] } : {}),
    ...(f.program !== "all" ? { programs: [f.program] } : {}),
    ...(f.campus !== "all" ? { locationIds: [f.campus] } : {}),
    ...(modalities.length && modalities.length < 3 ? { modalities } : {}),
    ...(outcomes ? { outcomes } : {}),
    ...(scope ? { scope } : {}),
    roster: {
      page: page + 1,
      limit: rosterLimit,
      sort: f.sort,
      direction: f.dir,
      ...(f.search.trim() ? { query: f.search.trim() } : {}),
    },
  };
}

/* ── View ──────────────────────────────────────────────────────────── */
export type Student = CampusPulseRosterRow;

export interface LadderRow {
  key: string; label: string; short: string;
  complete: boolean; started: boolean;
  total: number; active: number; dark: number | null; perStudent: number;
}

export interface View {
  report: CampusPulseReport;
  cohortKey: string;
  cohortLabel: string;
  /** False for the synthetic all-students cohort the server serves when no
   *  class year qualifies — the surface must not call that a class. */
  cohortFormed: boolean;
  n: number;
  nMeasured: number;
  directoryTotal: number | null;
  completeTerms: number;
  totalTerms: number;
  kpis: CampusPulseReport["kpis"];
  interactions: number;
  outsideTerms: number | null;
  ladder: LadderRow[];
  laneCount: Record<Lane, number>;
  depts: { key: string; label: string; kind: DeptKind; total: number; reach: number | null }[];
  programs: { key: string; label: string; students: number | null; interactions: number; share: number | null }[];
  staff: CampusPulseReport["breakdowns"]["staff"];
  roster: CampusPulseReport["roster"];
  frame: CampusPulseReport["frame"];
  coverage: CampusPulseReport["coverage"];
  visibility: CampusPulseReport["visibility"];
  cohorts: CampusPulseReport["cohorts"];
}

const nowMs = () => Date.now();
const dayEndMs = (isoDate: string) => new Date(`${isoDate}T23:59:59.999`).getTime();
const dayStartMs = (isoDate: string) => new Date(`${isoDate}T00:00:00`).getTime();

export function buildView(report: CampusPulseReport, cohortKey: string): View {
  const cohort = report.cohorts.find((c) => c.key === cohortKey) || report.cohorts[0] || null;
  const terms = report.terms;
  const t = nowMs();
  /* `endsOn` is INCLUSIVE of its closing day (contract confirmation 1), so a
   * term is complete once the window has passed the END of that day — not once
   * it has passed the day after. */
  const complete = terms.filter((x) => dayEndMs(x.endsOn) < t).length;

  const termRow = (i: number): LadderRow => {
    const term = terms[i];
    const ct = cohort?.terms.find((x) => x.termKey === term.key) || null;
    const students = cohort?.size ?? report.kpis.students;
    return {
      key: term.key, label: term.label, short: term.label.replace(/\s+/g, " "),
      complete: dayEndMs(term.endsOn) < t,
      started: dayStartMs(term.startsOn) <= t,
      total: ct?.interactions ?? 0,
      active: ct?.activeStudents ?? 0,
      dark: ct?.darkStudents ?? null,
      perStudent: students ? (ct?.interactions ?? 0) / students : 0,
    };
  };

  const modality = new Map(report.breakdowns.modality.map((r) => [r.key, r.interactions]));
  const noShows = report.roster.rows.reduce((a, r) => a + r.noShowCount, 0);
  const laneCount: Record<Lane, number> = {
    appt: modality.get("appointment") ?? 0,
    walkin: modality.get("queueEntry") ?? 0,
    event: modality.get("eventRegistration") ?? 0,
    noshow: noShows,
  };

  const students = report.kpis.students;
  const interactions =
    report.breakdowns.modality.reduce((a, r) => a + r.interactions, 0);

  return {
    report,
    cohortKey: cohort?.key ?? "all-students",
    cohortLabel: cohort?.label ?? "All students",
    cohortFormed: cohort?.formed ?? false,
    n: cohort?.size ?? students,
    nMeasured: report.coverage.population.measured,
    directoryTotal: report.coverage.population.directoryTotal,
    completeTerms: complete,
    totalTerms: terms.length,
    kpis: report.kpis,
    interactions,
    outsideTerms: report.window.interactionsOutsideTerms,
    ladder: terms.map((_, i) => termRow(i)),
    laneCount,
    depts: report.breakdowns.department.map((r) => ({
      key: r.key, label: r.label, kind: r.kind,
      total: r.interactions,
      reach: r.students !== null && students ? Math.round((r.students / students) * 100) : null,
    })),
    programs: report.breakdowns.program.map((r) => ({
      key: r.key, label: r.label, students: r.students, interactions: r.interactions, share: r.share,
    })),
    staff: report.breakdowns.staff,
    roster: report.roster,
    frame: report.frame,
    coverage: report.coverage,
    visibility: report.visibility,
    cohorts: report.cohorts,
  };
}

/* ── Coverage ──────────────────────────────────────────────────────────
 * The block the whole switch was for: what the report could not answer, in
 * the report's own words. Rendered as a panel, not a footnote. */
export const DIMENSION_LABEL: Record<string, string> = { // ui-config
  classYear: "Class year", program: "Program", campusOfRecord: "Campus of record",
  department: "Department", departmentKind: "Department kind (support / growth)",
  channel: "Channel", advisor: "Advisor", advisorBookableMinutes: "Advisor bookable hours",
  laneAppointment: "Appointments lane", laneWalkIn: "Walk-in lane", laneEvent: "Events lane",
  terms: "Academic terms",
};
export const VERDICT_LABEL: Record<CampusPulseCoverageVerdict, string> = { // ui-config
  available: "Measured", partial: "Partly measured", unavailable: "Not measured",
};

export function coverageRows(v: View): { key: string; label: string; verdict: CampusPulseCoverageVerdict }[] {
  return Object.entries(v.coverage.dimensions)
    .map(([key, verdict]) => ({ key, label: DIMENSION_LABEL[key] || key, verdict: verdict as CampusPulseCoverageVerdict }))
    .sort((a, b) => {
      const rank = { unavailable: 0, partial: 1, available: 2 } as const;
      return rank[a.verdict] - rank[b.verdict] || a.label.localeCompare(b.label);
    });
}

/* ── Findings ──────────────────────────────────────────────────────────
 * The server decides severity, n, value, threshold, the method line and the
 * unreadable reason. This module supplies only what the operation does not
 * author: the card's id and index, its read/impact/action/owner prose, and the
 * verb — and the prose is written against the SAME visibility decision the
 * response carries, so with `staffNames: false` no name reaches the copy. */
export interface Finding extends FindingCardModel {
  key: CampusPulseFindingKey;
  /** Scope this card can apply to the whole report. Null when the finding
   *  names no member set to narrow to. */
  scope: Scope;
  memberCount: number | null;
  verb: FindingVerb | null;
  /** Why the verb is disabled, when it is. */
  verbBlocked: string | null;
  namesStaff: boolean;
}

const CARD: Record<CampusPulseFindingKey, { id: string; index: string; headline: string }> = { // ui-config
  went_dark: { id: "dark", index: "2", headline: "went dark" },
  walk_ins_vs_supply: { id: "walk", index: "3", headline: "walk in rather than book" },
  advising_continuity: { id: "continuity", index: "4", headline: "without advising continuity" },
  caseload_vs_capacity: { id: "caseload", index: "5", headline: "caseload spread" },
  programs_trending_down: { id: "programs", index: "6", headline: "trending down" },
  data_quality: { id: "excluded", index: "7", headline: "cannot be placed in a cohort" },
};

const CATEGORY: Record<ApiFinding["category"], FindingCategory> = { // ui-config
  outreach: "outreach", schedule: "schedule", continuity: "continuity",
  staffing: "staffing", curriculum: "curriculum", dataQuality: "data",
};

/** `none` is a real, measured nothing — it must not read as a low-grade
 *  problem, so it maps to the kit's `clear`, never to `low`. */
function severityOf(s: ApiFinding["severity"]): Severity {
  return s === "none" ? "clear" : s;
}

const pct = (x: number | null) => (x === null ? "—" : `${Math.round(x * 100)}%`);
const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? "" : "s"}`;

/** JAI-1417: the batch send does not exist yet. Both contact verbs render
 *  disabled with the reason on the button rather than disappearing — a verb
 *  that vanishes reads as a finding with no action. */
export const CONTACT_VERB_BLOCKED =
  "Sending from a report needs the message batch command (JAI-1417) — the list is exportable meanwhile.";

export function buildFindings(v: View, staffNames: boolean): Finding[] {
  const r = v.report;
  const advisorLabel = (i: number, name: string | null) =>
    staffNames && name ? name : `Advisor ${String.fromCharCode(65 + i)}`;

  return r.findings.map((f) => {
    const card = CARD[f.key];
    const unreadable = f.severity === "unreadable";
    const n = f.n;
    const base = {
      key: f.key,
      id: card.id,
      index: card.index,
      headline: card.headline,
      severity: severityOf(f.severity),
      category: CATEGORY[f.category],
      /* Verbatim. The operation prints the thresholds it actually applied,
         including any small-sample cap, and the surface must not paraphrase
         a number it did not compute. */
      method: f.method,
      note: null as string | null,
      memberCount: f.memberCount,
      namesStaff: false,
      verbBlocked: null as string | null,
    };

    /* An unreadable card says what was removed and how to get it back. The
       reason is the server's sentence; nothing is invented on top of it. */
    if (unreadable) {
      return {
        ...base,
        value: "—",
        unit: "not readable under this scope",
        read: f.unreadableReason ?? "This finding's inputs are not available for this scope.",
        impact: "Not measurable here.",
        action: "Nothing to route until the inputs above are back.",
        owner: "—",
        rows: [],
        scope: null,
        verb: null,
      };
    }

    switch (f.key) {
      case "went_dark": {
        const dark = f.value === null ? 0 : Math.round(f.value * (n ?? 0));
        return {
          ...base,
          value: String(f.memberCount ?? dark),
          unit: `of ${plural(n ?? 0, "student")}`,
          read: (f.memberCount ?? dark) === 0
            ? "Nobody in this set has a completely silent term. Whatever else is going on, the record is continuous — read the engagement frame rather than the gaps."
            : "A complete term with no interaction anywhere on campus, over the last three complete terms. Silence in an already remedial record is the one this report is looking for.",
          impact: (f.memberCount ?? dark)
            ? `${pct(f.value)} of the measured population went dark in the lookback.`
            : "None.",
          action: (f.memberCount ?? dark)
            ? "Term-gap outreach before the next registration window."
            : "Nothing to route.",
          owner: "Assigned advisor.",
          rows: [
            { k: "Share of measured students", v: pct(f.value) },
            { k: "Next severity at", v: f.threshold === null ? "—" : pct(f.threshold) },
          ],
          scope: { finding: f.key } as Scope,
          verb: { id: "term-gap-outreach", label: t("reports.campusPulseData.sendOutreachToAll", "Send outreach to all {count}", { count: f.memberCount ?? dark }), kind: "message", done: t("reports.campusPulseData.outreachSent", "Outreach sent") },
          verbBlocked: CONTACT_VERB_BLOCKED,
        };
      }
      case "walk_ins_vs_supply": {
        const top = v.staff
          .filter((s) => s.bookableMinutesPerWeek !== null && s.walkInsPerAssignedStudent !== null)
          .slice(0, 3);
        return {
          ...base,
          value: String(f.memberCount ?? 0),
          unit: `${(f.memberCount ?? 0) === 1 ? "student walks" : "students walk"} in more than they book`,
          read: top.length
            ? "Walk-in demand against booked demand, weighed against the schedules that were available to book. The advisors whose bookable week is recorded are compared below; the coverage note names the rest."
            : "Walk-in demand against booked demand. The capacity arm is withheld: fewer than two advisors have a known, non-equal bookable week, so no schedule comparison can be made either way.",
          impact: `${pct(f.value)} of measured students take the desk over a booked slot.`,
          action: top.length
            ? "Open bookable hours on the thinnest schedule first."
            : "Record bookable hours against advisor availability; until then this card cannot say whether to change schedules or the booking journey.",
          owner: "Advising ops / scheduling.",
          rows: top.map((s, i) => ({
            k: `${advisorLabel(i, s.label)} · ${Math.round((s.bookableMinutesPerWeek ?? 0) / 60)}h/wk`,
            v: `${s.walkInsPerAssignedStudent} walk-ins/student`,
          })),
          scope: { finding: f.key } as Scope,
          verb: { id: "booking-journey-invite", label: t("reports.campusPulseData.inviteToBook", "Invite {count} to book", { count: f.memberCount ?? 0 }), kind: "message", done: t("reports.campusPulseData.invitesSent", "Invites sent") },
          verbBlocked: CONTACT_VERB_BLOCKED,
          namesStaff: staffNames && top.length > 0,
        };
      }
      case "advising_continuity":
        return {
          ...base,
          value: String(f.memberCount ?? 0),
          unit: (f.memberCount ?? 0) === 1 ? "student" : "students",
          read: (f.memberCount ?? 0) === 0
            ? "Every measured student has been seen by the advisor they hold as advisor of record. Continuity holds — nothing to chase here."
            : "Students who either never met their advisor of record, or hold no advisor at all. Continuity of advice cannot be assumed for any of them.",
          impact: `${pct(f.value)} of measured students, ${f.memberCount ?? 0} of ${n ?? 0}.`,
          action: (f.memberCount ?? 0)
            ? "Re-point each assignment to whoever the student actually sees, or move the assigned advisor's slots to where the student can reach them."
            : "Nothing to route.",
          owner: "Advising ops.",
          rows: [{ k: "Next severity at", v: f.threshold === null ? "—" : pct(f.threshold) }],
          scope: { finding: f.key } as Scope,
          /* `updateAssignedCustomer` is a real command — this verb is NOT
             blocked by JAI-1417. */
          verb: { id: "repoint-assignment", label: t("reports.campusPulseData.repointAssignments", "Re-point {count, plural, one {# assignment} other {# assignments}}", { count: f.memberCount ?? 0 }), kind: "activity", done: t("reports.campusPulseData.repointRequested", "Re-point requested") },
        };
      case "caseload_vs_capacity": {
        const rank = v.staff
          .filter((s) => s.assignedStudents !== null && s.bookableMinutesPerWeek)
          .slice(0, 4);
        return {
          ...base,
          value: f.value === null ? "—" : `${Math.round(f.value * 10) / 10}×`,
          unit: "spread, students per bookable hour",
          read: "Assignment can be even while capacity is not: the same caseload pressed into a much shorter bookable week is a different job. The four advisors below are the evidence.",
          impact: "The shortest bookable week sets the ceiling on the caseload it carries.",
          action: "Move caseload toward the widest schedule, or extend the thinnest bookable week.",
          owner: "You, with advising ops.",
          rows: rank.map((s, i) => ({
            k: `${advisorLabel(i, s.label)} · ${plural(s.assignedStudents ?? 0, "student")}`,
            v: `${Math.round(((s.assignedStudents ?? 0) / Math.max(1, (s.bookableMinutesPerWeek ?? 0) / 60)) * 10) / 10}/h · ${s.deliveredInteractions ?? 0} seen`,
          })),
          scope: null,
          verb: { id: "caseload-review", label: t("reports.campusPulseData.label5", "Assign caseload review"), kind: "handoff", done: "Assigned to advising ops" },
          namesStaff: staffNames && rank.length > 0,
        };
      }
      case "programs_trending_down": {
        const lead = v.programs[0];
        return {
          ...base,
          value: String(f.value === null ? 0 : Math.round(f.value)),
          unit: `of ${plural(v.programs.length, "program")}`,
          read: (f.value ?? 0) > 0
            ? "A whole-program slide is a curriculum or advising-model question, not a per-student one. A program counts as down when more of its students are falling than rising over the complete terms."
            : "No program has more students falling than rising. Decline in this set is individual, not structural — work the student lists rather than the program.",
          impact: (f.value ?? 0) > 0
            ? `${Math.round(f.value ?? 0)} of ${v.programs.length} programs are trending down.`
            : "None.",
          action: (f.value ?? 0) > 0
            ? `Take ${lead ? lead.label : "the leading program"} to its director with the by-program table below; ask what changed in the terms where the slide starts.`
            : "Nothing to route. Re-check when the next term completes.",
          owner: "Program director.",
          rows: v.programs.slice(0, 3).map((p) => ({ k: p.label, v: `${p.interactions.toLocaleString()} interactions` })),
          scope: null,
          verb: (f.value ?? 0) > 0
            ? { id: "program-review", label: t("reports.campusPulseData.sendToProgramDirector", "Send to the program director"), kind: "handoff", done: t("reports.campusPulseData.sentToProgramDirector", "Sent to program director") }
            : null,
        };
      }
      case "data_quality": {
        const ex = v.coverage.population.excluded;
        return {
          ...base,
          value: String(f.value === null ? 0 : Math.round(f.value * (n ?? 0))),
          unit: "directory rows excluded",
          read: "Rows the report could not place. They are absent from every number on this page — not counted as zero.",
          impact: `Every median and share here is computed on ${v.nMeasured.toLocaleString()} measured records${v.directoryTotal !== null ? ` of ${v.directoryTotal.toLocaleString()} in the directory` : ""}.`,
          action: "Backfill the missing fields on the source feed; these students are invisible to the report until it lands.",
          owner: "SIS integration / registrar.",
          rows: [
            { k: "No class year", v: ex.missingClassYear === null ? "—" : String(ex.missingClassYear) },
            { k: "No name", v: ex.missingName === null ? "—" : String(ex.missingName) },
            { k: "Measured students", v: v.nMeasured.toLocaleString() },
          ],
          scope: null,
          verb: null,
        };
      }
      default:
        return { ...base, value: "—", unit: "", read: "", impact: "", action: "", owner: "—", rows: [], scope: null, verb: null };
    }
  });
}

/* ── Roster chips ──────────────────────────────────────────────────────
 * Why a student is in the list — the factors behind the flag, every one of
 * them a field the server computed, so a chip shows its reasoning instead of
 * asserting a verdict. */
export function whyFlagged(s: Student, v: View): { label: string; tone: "warn" | "info" | "danger" | "neutral" }[] {
  const out: { label: string; tone: "warn" | "info" | "danger" | "neutral" }[] = [];
  if (s.darkTermsInLookback) {
    out.push({
      label: s.darkTermsInLookback === 1 ? "dark 1 of last 3 terms" : `dark ${s.darkTermsInLookback} of last 3 terms`,
      tone: "danger",
    });
  }
  const medSupport = v.frame?.medianSupportShare ?? null;
  if (s.supportShare !== null && medSupport !== null && s.supportShare >= medSupport) {
    out.push({ label: t("reports.campusPulseData.label6", "support-heavy record"), tone: "warn" });
  }
  if (s.engagementTrend !== null && s.engagementTrend < -0.15) {
    out.push({ label: t("reports.campusPulseData.falling", "falling ({trend})", { trend: (Math.round(s.engagementTrend * 10) / 10).toFixed(1) }), tone: "danger" });
  }
  if (s.walkInShare !== null && s.walkInShare >= 0.5) {
    out.push({ label: t("reports.campusPulseData.walkInShare", "{percent}% walk-in", { percent: Math.round(s.walkInShare * 100) }), tone: "warn" });
  }
  if (s.noShowCount >= 3) out.push({ label: t("reports.campusPulseData.noShowsCount", "{count, plural, one {# no-show} other {# no-shows}}", { count: s.noShowCount }), tone: "info" });
  return out.slice(0, 3);
}

export const QUADRANT_LABEL: Record<CampusPulseQuadrantKey, string> = { // ui-config
  heavy_support: "Heavy support",
  building: "Building",
  quiet_struggling: "Quiet, struggling",
  low_touch: "Low touch",
};
export const QUADRANT_NOTE: Record<CampusPulseQuadrantKey, string> = { // ui-config
  heavy_support: "High volume, mostly problem-solving. Real need, already visible.",
  building: "High volume, mostly development. Healthy — protect it.",
  quiet_struggling: "Low volume and what there is, is remedial. The blind spot.",
  low_touch: "Low volume, none of it remedial. Probably fine.",
};

/* ── Dates ─────────────────────────────────────────────────────────── */
export function dShort(iso: string) {
  const d = new Date(iso.length > 10 ? iso : `${iso}T12:00:00`);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}
export function dLong(iso: string) {
  const d = new Date(iso.length > 10 ? iso : `${iso}T12:00:00`);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}
